import { differenceInDays, parseISO } from 'date-fns';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { ScrollToApplyButton } from '#/components/ApplyButton/ScrollToApplyButton';
import { Divider } from '#/components/Divider/Divider';
import { Hide } from '#/components/Hide/Hide';
import { Text } from '#/components/Text/Text';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useAppConfig } from '#/config/AppConfigContext';
import { useIsClient } from '#/hooks/useIsClient';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import {
  convertInstantToIdealistWallClockDate,
  formatRelativeToNow,
  formatCalendarDateShort,
} from '#/utils/date';

type ListingSidebarContentProps = {
  firstPublished: ApiISODateTimeString | null;
  listingViewStatus: ListingViewStatus;
  showScrollToApplyButton: boolean;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  applied: boolean;
};

export function ListingViewSidebarContent({
  firstPublished,
  listingViewStatus,
  showScrollToApplyButton,
  listingType,
  applied,
}: ListingSidebarContentProps) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();
  const isClient = useIsClient();

  return (
    <>
      {firstPublished && listingViewStatus !== ListingViewStatus.PREVIEW && (
        <div>
          {differenceInDays(parseISO(firstPublished), new Date()) < 2 && (
            <>
              <Text
                fontWeight="semiBold"
                color="mediumContentGrey"
                borderBottom="4px solid"
                borderColor="brandYellow"
                display="inline-block"
                lineHeight="6px"
                ml="0px"
                mr="12px"
                p="0px"
              >
                {i18n('New')}
              </Text>
              <Text
                display={[
                  'inline-block',
                  'inline-block',
                  'inline-block',
                  'none',
                ]}
                color="lightContentGrey"
                pl={2}
                pr={2}
              >
                |
              </Text>
            </>
          )}
          <Text display="inline-block" color="lightContentGrey">
            {
              /* TRANSLATORS: Listing page - time published */
              i18n('Published %(timeAgo)s', {
                timeAgo: isClient
                  ? formatRelativeToNow({
                      date: firstPublished,
                      locale,
                      dtOverride: appConfig.idealist.dtOverride,
                    })
                  : formatCalendarDateShort({
                      date: convertInstantToIdealistWallClockDate(
                        new Date(firstPublished),
                      ),
                      locale,
                    }),
              })
            }
          </Text>
        </div>
      )}

      {listingType !== 'EVENT' &&
        listingViewStatus !== ListingViewStatus.EXPIRED &&
        showScrollToApplyButton && (
          <Hide xs sm md>
            <Tooltip
              content={i18n("You can't take this action in Preview Mode")}
              triggerCondition={listingViewStatus === ListingViewStatus.PREVIEW}
              variant="filled"
              placement="bottom-center"
              size="large"
            >
              <div
                style={{
                  marginTop: 24,
                  cursor:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'not-allowed'
                      : undefined,
                  pointerEvents:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'none'
                      : undefined,
                }}
              >
                <ScrollToApplyButton
                  tabIndex={
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? -1
                      : undefined
                  }
                  applied={applied}
                >
                  {listingType === 'VOLOP' ? i18n('I Want to Help') : undefined}
                </ScrollToApplyButton>
              </div>
            </Tooltip>
          </Hide>
        )}

      <Divider marginTop={24} marginBottom={24} />
    </>
  );
}
